import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SalesRepList from '../components/SalesRepList';
import SalesRepMap from '../components/SalesRepMap';
import VisitDetailsPane from '../components/VisitDetailsPane';
import RepProfileTab from '../components/RepProfileTab';
import RepNotesTab from '../components/RepNotesTab';
import RepExpensesTab from '../components/RepExpensesTab';
import HospitalManagement from '../components/HospitalManagement';
import { fetchAllReps, fetchRepData, fetchRepProfile, logoutAdmin, getAdminUser, createRep } from '../services/api';
import { LogOut, RefreshCw, Filter, Calendar, MapPin, Users, Map, User, MessageSquare, DollarSign, Plus, X, Building2 } from 'lucide-react';

export default function Dashboard() {
  const [reps, setReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [repData, setRepData] = useState(null);
  const [repProfile, setRepProfile] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repDataLoading, setRepDataLoading] = useState(false);
  const [repProfileLoading, setRepProfileLoading] = useState(false);
  // Empty date means "no filter" – backend will return last 30 days
  const [date, setDate] = useState('');
  const [townFilter, setTownFilter] = useState('');
  const [showVisitDetails, setShowVisitDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('tracking'); // tracking | profile | notes | expenses | hospitals
  const [showCreateRepModal, setShowCreateRepModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', phone: '', town: '', salaryAgreed: '' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const adminUser = getAdminUser();

  const loadReps = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (townFilter) params.town = townFilter;
      const result = await fetchAllReps(params);
      if (result.success) {
        setReps(result.data.reps || []);
      }
    } catch (err) {
      // Reps load error handled silently
    } finally {
      setLoading(false);
    }
  }, [date, townFilter]);

  const loadRepProfile = useCallback(async (repId) => {
    setRepProfileLoading(true);
    try {
      const result = await fetchRepProfile(repId);
      if (result.success) {
        setRepProfile(result.data);
      }
    } catch (err) {
      // Rep profile load error handled silently
    } finally {
      setRepProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReps();
  }, [loadReps]);

  const handleSelectRep = async (rep) => {
    setSelectedRep(rep);
    setSelectedVisit(null);
    setShowVisitDetails(false);
    setActiveTab('tracking'); // reset to tracking tab
    setRepDataLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      const result = await fetchRepData(rep.id, params);
      if (result.success) {
        setRepData(result.data);
      }
    } catch (err) {
      // Rep data load error handled silently
    } finally {
      setRepDataLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (selectedRep && (tab === 'profile' || tab === 'notes' || tab === 'expenses')) {
      loadRepProfile(selectedRep.id);
    }
  };

  const handleProfileUpdate = (updatedRep) => {
    setSelectedRep(updatedRep);
    // Update rep in list
    setReps((prev) => prev.map((r) => (r.id === updatedRep.id ? { ...r, ...updatedRep } : r)));
    // Update repProfile if loaded
    if (repProfile && repProfile.rep.id === updatedRep.id) {
      setRepProfile((prev) => ({ ...prev, rep: { ...prev.rep, ...updatedRep } }));
    }
  };

  const handleNoteAdded = (newNote) => {
    if (repProfile) {
      setRepProfile((prev) => ({ ...prev, notes: [newNote, ...prev.notes] }));
    }
  };

  const handleExpenseAdded = (newExpense) => {
    if (repProfile) {
      setRepProfile((prev) => ({ ...prev, expenses: [newExpense, ...prev.expenses] }));
    }
  };

  const handleCreateRep = async () => {
    const { name, email, password, phone, town, salaryAgreed } = createForm;
    if (!name || !email || !password) {
      alert('Name, email, and password are required');
      return;
    }
    const payload = { name, email, password, phone: phone || null, town: town || null };
    if (salaryAgreed && salaryAgreed.trim()) {
      const parsed = parseFloat(salaryAgreed);
      if (Number.isNaN(parsed) || parsed < 0) {
        alert('Invalid salary amount');
        return;
      }
      payload.salaryAgreed = parsed;
    }
    setCreating(true);
    try {
      const result = await createRep(payload);
      if (result.success) {
        setReps((prev) => [result.data.rep, ...prev]);
        setShowCreateRepModal(false);
        setCreateForm({ name: '', email: '', password: '', phone: '', town: '', salaryAgreed: '' });
      } else {
        alert(result.error || 'Failed to create rep');
      }
    } catch (err) {
      // Rep creation error handled silently
      alert('Failed to create rep');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectVisit = (visit) => {
    setSelectedVisit(visit);
    setShowVisitDetails(true);
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/login');
  };

  const towns = [...new Set(reps.map((r) => r.town).filter(Boolean))];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="bg-primary-900 text-white px-6 py-3 flex items-center justify-between shadow-lg z-10">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold tracking-wide">CleoCure</h1>
          <span className="text-primary-200 text-sm hidden sm:inline">Admin Dashboard</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Date Filter */}
          <div className="flex items-center gap-2 bg-primary-800 rounded-lg px-3 py-1.5">
            <Calendar size={16} className="text-primary-300" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent text-white text-sm outline-none cursor-pointer"
            />
          </div>

          {/* Town Filter */}
          <div className="flex items-center gap-2 bg-primary-800 rounded-lg px-3 py-1.5">
            <Filter size={16} className="text-primary-300" />
            <select
              value={townFilter}
              onChange={(e) => setTownFilter(e.target.value)}
              className="bg-transparent text-white text-sm outline-none cursor-pointer"
            >
              <option value="" className="text-gray-900">All Towns</option>
              {towns.map((t) => (
                <option key={t} value={t} className="text-gray-900">{t}</option>
              ))}
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={loadReps}
            className="p-2 hover:bg-primary-800 rounded-lg transition"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>

          {/* Create Rep */}
          <button
            onClick={() => setShowCreateRepModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1"
          >
            <Plus size={16} /> Create Rep
          </button>

          {/* User & Logout */}
          <div className="flex items-center gap-3 border-l border-primary-700 pl-4">
            <span className="text-sm text-primary-200 hidden md:inline">
              {adminUser?.name || 'Admin'}
            </span>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-red-600 rounded-lg transition"
              title="Sign Out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Summary Bar */}
      <div className="bg-white border-b px-6 py-2 flex items-center gap-6 text-sm">
        <div className="flex items-center gap-1.5 text-gray-600">
          <Users size={14} />
          <span><strong>{reps.length}</strong> reps</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <MapPin size={14} />
          <span><strong>{reps.reduce((sum, r) => sum + (r.total_visits || 0), 0)}</strong> total visits</span>
        </div>
        <div className="flex items-center gap-1.5 text-green-600">
          <span>🏆 <strong>{reps.filter((r) => r.bonus_eligible).length}</strong> bonus eligible</span>
        </div>
        {selectedRep && (
          <div className="ml-auto text-primary-900 font-medium">
            Viewing: {selectedRep.name}
          </div>
        )}
      </div>

      {/* Main Content: Left Pane (30%) + Right Pane (70%) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Pane - Sales Rep List */}
        <div className="w-[30%] min-w-[300px] border-r bg-white overflow-y-auto">
          <SalesRepList
            reps={reps}
            loading={loading}
            selectedRep={selectedRep}
            onSelectRep={handleSelectRep}
          />
        </div>

        {/* Right Pane - Map (70%) */}
        <div className="flex-1 relative flex flex-col">
          {/* Tab Navigation */}
          {selectedRep && (
            <div className="bg-white border-b px-4 flex items-center gap-1">
              <button
                onClick={() => handleTabChange('tracking')}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-1.5 ${
                  activeTab === 'tracking'
                    ? 'bg-primary-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Map size={16} /> Tracking
              </button>
              <button
                onClick={() => handleTabChange('profile')}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-1.5 ${
                  activeTab === 'profile'
                    ? 'bg-primary-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <User size={16} /> Profile
              </button>
              <button
                onClick={() => handleTabChange('notes')}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-1.5 ${
                  activeTab === 'notes'
                    ? 'bg-primary-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MessageSquare size={16} /> Notes
              </button>
              <button
                onClick={() => handleTabChange('expenses')}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-1.5 ${
                  activeTab === 'expenses'
                    ? 'bg-primary-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <DollarSign size={16} /> Expenses
              </button>
              <button
                onClick={() => handleTabChange('hospitals')}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-1.5 ${
                  activeTab === 'hospitals'
                    ? 'bg-primary-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Building2 size={16} /> Hospitals
              </button>
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 relative">
            {selectedRep && activeTab === 'tracking' && (
              <SalesRepMap
                repData={repData}
                selectedRep={selectedRep}
                loading={repDataLoading}
                onSelectVisit={handleSelectVisit}
                date={date}
              />
            )}
            {selectedRep && activeTab === 'profile' && (
              <div className="h-full overflow-y-auto bg-gray-50">
                {repProfileLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-900 mx-auto mb-2"></div>
                    <p className="text-sm">Loading profile...</p>
                  </div>
                ) : repProfile ? (
                  <RepProfileTab rep={repProfile.rep} onUpdate={handleProfileUpdate} />
                ) : null}
              </div>
            )}
            {selectedRep && activeTab === 'notes' && (
              <div className="h-full overflow-y-auto bg-gray-50">
                {repProfileLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-900 mx-auto mb-2"></div>
                    <p className="text-sm">Loading notes...</p>
                  </div>
                ) : repProfile ? (
                  <RepNotesTab repId={selectedRep.id} notes={repProfile.notes} onNoteAdded={handleNoteAdded} />
                ) : null}
              </div>
            )}
            {selectedRep && activeTab === 'expenses' && (
              <div className="h-full overflow-y-auto bg-gray-50">
                {repProfileLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-900 mx-auto mb-2"></div>
                    <p className="text-sm">Loading expenses...</p>
                  </div>
                ) : repProfile ? (
                  <RepExpensesTab repId={selectedRep.id} expenses={repProfile.expenses} onExpenseAdded={handleExpenseAdded} />
                ) : null}
              </div>
            )}

            {selectedRep && activeTab === 'hospitals' && (
              <HospitalManagement />
            )}

            {/* Visit Details Overlay (only on Tracking tab) */}
            {showVisitDetails && selectedVisit && activeTab === 'tracking' && (
              <VisitDetailsPane
                visit={selectedVisit}
                onClose={() => {
                  setShowVisitDetails(false);
                  setSelectedVisit(null);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Create Rep Modal */}
      {showCreateRepModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Create New Sales Rep</h3>
              <button
                onClick={() => setShowCreateRepModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Name"
                value={createForm.name}
                onChange={(e) => setCreateForm((c) => ({ ...c, name: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <input
                type="email"
                placeholder="Email"
                value={createForm.email}
                onChange={(e) => setCreateForm((c) => ({ ...c, email: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <input
                type="password"
                placeholder="Password"
                value={createForm.password}
                onChange={(e) => setCreateForm((c) => ({ ...c, password: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={createForm.phone}
                onChange={(e) => setCreateForm((c) => ({ ...c, phone: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder="Town (optional)"
                value={createForm.town}
                onChange={(e) => setCreateForm((c) => ({ ...c, town: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Agreed Salary (optional)"
                value={createForm.salaryAgreed}
                onChange={(e) => setCreateForm((c) => ({ ...c, salaryAgreed: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreateRepModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRep}
                disabled={creating}
                className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Rep'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
